import React, { useState } from 'react';
import { Divider } from 'antd';
import styled from 'styled-components';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';

import { SearchFilter } from 'components/app/FilterBar/filters';
import QueryResult from 'components/util/QueryResult';
import { OpSectionListItem, OpSectionListItemMeta } from '../../components';
import { OnSelectTriggerFunc } from '../../OpEditor';
import { CloudVendorIcon } from 'components/cloudVendors';
import { AutomationEventDefinition } from 'typings';
import DopeIcon from 'components/ui/DopeIcon';

const OP_TRIGGERS_QUERY = gql`
  query OpTriggers($eventDefinitionId: [String], $pageNumber: Int, $pageSize: Int, $search: String) {
    eventDefinitions(id: $eventDefinitionId, pageNumber: $pageNumber, pageSize: $pageSize, search: $search) {
      nodes {
        id
        name
        key
        description
        vendor
        vendorProduct
        severity
        filters {
          key
          type
          required
          name
          options {
            name
            path
            patterns
            foundByProduct
          }
        }
        context
        labels
        eventSource {
          id
        }
      }
    }
  }
`;

const TriggerListRoot = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

interface Props {
  onSelect: OnSelectTriggerFunc;
}

function SelectTrigger(props: Props) {
  const { onSelect } = props;

  const [triggerSearchStr, setTriggerSearchStr] = useState<string | null>(null);
  const { loading, error, data } = useQuery(OP_TRIGGERS_QUERY, {
    variables: { pageSize: 1000, search: triggerSearchStr }
  }); // pageSize: 1000 until paging solution

  return (
    <>
      <SearchFilter
        classes={{ root: 'search-input' }}
        search={triggerSearchStr || ''}
        onSearchChange={(str) => setTriggerSearchStr(str && str.length > 0 ? str : null)}
        placeholder="Search available Triggers"
      />

      <Divider />

      <QueryResult
        loading={loading}
        error={error}
        data={data}
        loadingHeight={400}
        loadingCenterVertically={false}
        entityName="Triggers"
      >
        {() => {
          const eventDefinitions: AutomationEventDefinition[] = data?.eventDefinitions?.nodes || [];

          return (
            <TriggerListRoot>
              <OpSectionListItem
                key="all-findings"
                title="All Findings"
                description="All finding events."
                icon={<DopeIcon name="EVENT" size={30} />}
                onClick={() => onSelect({ eventDefinition: undefined, isAllFindingsSelected: true })}
              />

              {eventDefinitions.map((eventDefinition) => {
                const { vendor, vendorProduct, severity } = eventDefinition;
                const labels = eventDefinition.labels || [];

                return (
                  <OpSectionListItem
                    key={eventDefinition.id}
                    title={eventDefinition.name}
                    description={eventDefinition.description}
                    icon={<CloudVendorIcon vendor={eventDefinition.vendor} />}
                    onClick={onSelect ? () => onSelect({ eventDefinition }) : undefined}
                    meta={
                      <>
                        {labels.map((label, idx) => (
                          <OpSectionListItemMeta key={`${idx}_${label}`} value={label} />
                        ))}
                      </>
                    }
                    metaBottom={
                      <>
                        {vendor && vendorProduct && (
                          <OpSectionListItemMeta
                            size="small"
                            label="Event Source:"
                            value={`${vendor}:${vendorProduct}`}
                          />
                        )}
                        {severity !== null && severity !== undefined && (
                          <OpSectionListItemMeta size="small" label="Severity:" value={`${severity}`} />
                        )}
                      </>
                    }
                  />
                );
              })}
            </TriggerListRoot>
          );
        }}
      </QueryResult>
    </>
  );
}

export default SelectTrigger;
